import { Badge, Button, Flex, H3, P } from '@dnb/eufemia';
import { type JSX, useState } from 'react';

import Card from '@/components/Card';
import { useBackend } from '@/hooks/useBackend';
import { ApiCiamClientResponse } from '@/openapi/schema';
import { ErrorMessage } from '@/pages/ImportClients/components/ErrorMessage';
import { handleSWRErrorLocally } from '@/utils/swrOpenapiUtils';

import style from './index.module.css';

export type CiamClientTileProps = {
  client: ApiCiamClientResponse;
  apiSlug: string;
};

export default function CiamClientTile({ client, apiSlug }: CiamClientTileProps): JSX.Element {
  const [showExtraScopes, setShowExtraScopes] = useState<boolean>(false);

  const {
    data: extraScopesResponse,
    isLoading: extraScopesLoading,
    error: extraScopesError,
  } = useBackend(
    '/apis/{apiSlug}/ciam-clients/{clientId}/scopes',
    apiSlug && showExtraScopes ? { params: { path: { apiSlug: apiSlug, clientId: client.id } } } : null,
    { revalidateIfStale: false, revalidateOnFocus: false, revalidateOnReconnect: false, ...handleSWRErrorLocally },
  );

  const hasExtraScopes =
    showExtraScopes &&
    !extraScopesLoading &&
    !extraScopesError &&
    extraScopesResponse &&
    extraScopesResponse.scopes.length > 0;

  const extraScopes = hasExtraScopes ? extraScopesResponse.scopes.filter((s) => !client.scopes.includes(s)) : [];

  return (
    <Card className={style['CiamClientTile']}>
      <Flex.Container stretch>
        <Flex.Item grow>
          <H3 bottom="small">{client.name || 'Unnamed Client'}</H3>
        </Flex.Item>
        <Badge content={client.liveMode ? 'Live' : 'Test'} status={client.liveMode ? 'positive' : 'default'} />
      </Flex.Container>
      <div className={style['InformationGrid']}>
        <P weight="medium">Client ID:</P>
        <P>{client.id}</P>
        <P weight="medium">Scopes:</P>
        <div className="flex justify-between items-end">
          <div>
            {client.scopes.map((s) => (
              <P key={s}>{s}</P>
            ))}
            {extraScopes.map((s) => (
              <P key={s}>{s}</P>
            ))}
            {showExtraScopes && extraScopesError && <ErrorMessage errorMessage="Failed to load all scopes" />}
            <Button
              disabled={extraScopesLoading}
              icon={showExtraScopes ? 'chevron_up' : 'chevron_down'}
              iconPosition="left"
              onClick={() => {
                setShowExtraScopes(!showExtraScopes);
              }}
              size="small"
              text={showExtraScopes ? 'Show only current API scopes' : 'Show all scopes'}
              top="x-small"
              variant="secondary"
            />
          </div>
          <P className={style['CiamOnlyLabel']} right="small" size="x-small">
            Not imported to portal
          </P>
        </div>
      </div>
    </Card>
  );
}
